/**
 * CalculsPage — Configuration des paramètres de calcul astrologique
 *
 * Tableaux éditables pour orbes, configurations, dominante.
 * Admin seulement.
 */

import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';

interface OrbeRow {
  conjonction: number;
  opposition: number;
  carre: number;
  trigone: number;
  sextile: number;
  semiSextile: number;
  semiCarre: number;
  sesquicarre: number;
}

interface CalculsConfig {
  orbes: {
    luminaires: OrbeRow;
    personnelles: OrbeRow;
    lentes: OrbeRow;
  };
  configurations: {
    stelliumMin: number;
    concentrationMin: number;
    grandCarreOrbe: number;
    grandTrigoneOrbe: number;
    tCarreOrbe: number;
    yodOrbeQuinconce: number;
    yodOrbeSextile: number;
  };
  dominante: {
    orbeConjonctionLuminairePerso: number;
    orbeConjonctionLuminaireLente: number;
    orbeConjonctionAngle: number;
  };
}

const ASPECT_LABELS: [keyof OrbeRow, string][] = [
  ['conjonction', 'Conj.'],
  ['opposition', 'Oppo.'],
  ['carre', 'Carré'],
  ['trigone', 'Trigone'],
  ['sextile', 'Sextile'],
  ['semiSextile', 'Semi-sex.'],
  ['semiCarre', 'Semi-car.'],
  ['sesquicarre', 'Sesqui.'],
];

const ROW_LABELS: [keyof CalculsConfig['orbes'], string][] = [
  ['luminaires', 'Luminaires (Sol/Lune)'],
  ['personnelles', 'Planètes perso'],
  ['lentes', 'Planètes lentes'],
];

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: '1.25rem',
  marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

export function CalculsPage() {
  const [config, setConfig] = useState<CalculsConfig | null>(null);
  const [, setDefaults] = useState<CalculsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch<{ calculs: CalculsConfig; defaults: CalculsConfig }>('/settings/calculs')
      .then((res) => {
        if (res.success && res.data) {
          setConfig(res.data.calculs);
          setDefaults(res.data.defaults);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  function updateOrbe(category: keyof CalculsConfig['orbes'], aspect: keyof OrbeRow, value: number) {
    if (!config) return;
    setConfig({
      ...config,
      orbes: {
        ...config.orbes,
        [category]: { ...config.orbes[category], [aspect]: value },
      },
    });
  }

  function updateConfig(section: 'configurations' | 'dominante', key: string, value: number) {
    if (!config) return;
    setConfig({
      ...config,
      [section]: { ...config[section], [key]: value },
    });
  }

  async function handleSave() {
    if (!config) return;
    setSaving(true);
    setError('');
    setSaved(false);

    const res = await apiFetch('/settings/calculs', {
      method: 'PUT',
      body: JSON.stringify({ calculs: config }),
    });

    if (res.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setError(res.error ?? 'Erreur de sauvegarde');
    }
    setSaving(false);
  }

  async function handleReset() {
    if (!confirm('Réinitialiser tous les paramètres aux valeurs par défaut ?')) return;

    const res = await apiFetch<CalculsConfig>('/settings/calculs/reset', { method: 'POST' });
    if (res.success && res.data) {
      setConfig(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        Chargement...
      </div>
    );
  }

  if (!config) return null;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.4rem', fontWeight: 700, color: colors.text }}>
        <i className="bi bi-sliders" style={{ color: colors.accent, marginRight: 8 }} />
        Paramètres de calcul
      </h1>

      {/* Orbes par aspect */}
      <div style={card}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>
          Orbes par aspect et type de planète
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${colors.border}` }}></th>
                {ASPECT_LABELS.map(([, label]) => (
                  <th key={label} style={{ textAlign: 'center', padding: '6px 4px', borderBottom: `2px solid ${colors.border}`, fontSize: '0.75rem' }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROW_LABELS.map(([key, label]) => (
                <tr key={key}>
                  <td style={{ padding: '6px 8px', fontWeight: 500, borderBottom: `1px solid ${colors.bgPage}` }}>{label}</td>
                  {ASPECT_LABELS.map(([aspect]) => (
                    <td key={aspect} style={{ textAlign: 'center', padding: '4px', borderBottom: `1px solid ${colors.bgPage}` }}>
                      <input
                        type="number"
                        min={0}
                        max={15}
                        step={0.5}
                        value={config.orbes[key][aspect]}
                        onChange={(e) => updateOrbe(key, aspect, Number(e.target.value))}
                        style={{
                          width: 50, textAlign: 'center', padding: '4px',
                          border: `1px solid ${colors.border}`, borderRadius: 4,
                          fontFamily: 'inherit', fontSize: '0.8rem',
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Configurations */}
      <div style={card}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>
          Seuils de configurations
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {([
            ['stelliumMin', 'Stellium (min planètes)'],
            ['concentrationMin', 'Concentration (min planètes)'],
            ['grandCarreOrbe', 'Grand Carré (orbe max °)'],
            ['grandTrigoneOrbe', 'Grand Trigone (orbe max °)'],
            ['tCarreOrbe', 'T-Carré (orbe max °)'],
            ['yodOrbeQuinconce', 'Yod — quinconce (orbe °)'],
            ['yodOrbeSextile', 'Yod — sextile (orbe °)'],
          ] as const).map(([key, label]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: '0.85rem' }}>{label}</label>
              <input
                type="number"
                min={0}
                max={15}
                step={0.5}
                value={config.configurations[key]}
                onChange={(e) => updateConfig('configurations', key, Number(e.target.value))}
                style={{
                  width: 60, textAlign: 'center', padding: '4px',
                  border: `1px solid ${colors.border}`, borderRadius: 4,
                  fontFamily: 'inherit', fontSize: '0.85rem',
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Dominante planétaire */}
      <div style={card}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>
          Dominante planétaire
        </h2>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {([
            ['orbeConjonctionLuminairePerso', 'Orbe conj. luminaire — perso (°)'],
            ['orbeConjonctionLuminaireLente', 'Orbe conj. luminaire — lentes (°)'],
            ['orbeConjonctionAngle', 'Orbe conj. angle (°)'],
          ] as const).map(([key, label]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: '0.85rem' }}>{label}</label>
              <input
                type="number"
                min={0}
                max={15}
                step={0.5}
                value={config.dominante[key]}
                onChange={(e) => updateConfig('dominante', key, Number(e.target.value))}
                style={{
                  width: 60, textAlign: 'center', padding: '4px',
                  border: `1px solid ${colors.border}`, borderRadius: 4,
                  fontFamily: 'inherit', fontSize: '0.85rem',
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px', borderRadius: 6, border: 'none',
            background: colors.accent, color: colors.text, fontSize: '0.9rem',
            fontWeight: 600, cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit',
          }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button
          onClick={handleReset}
          style={{
            padding: '10px 24px', borderRadius: 6,
            border: `1px solid ${colors.border}`, background: '#fff',
            color: colors.textMid, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Réinitialiser par défaut
        </button>
        {saved && (
          <span style={{ color: '#166534', fontSize: '0.85rem' }}>
            <i className="bi bi-check-circle" style={{ marginRight: 4 }} /> Enregistré
          </span>
        )}
        {error && (
          <span style={{ color: '#991b1b', fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-circle" style={{ marginRight: 4 }} /> {error}
          </span>
        )}
      </div>
    </div>
  );
}
